import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, Mail, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import { formatApiError } from "@/services/api";

export default function AdminLogin() {
  const { login, completeOtpLogin, user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [challenge, setChallenge] = useState("");
  const [loading, setLoading] = useState(false);
  const [deployInfo, setDeployInfo] = useState(null);

  React.useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
        if (!base) return;
        const [root, fee] = await Promise.all([
          fetch(`${base}/api/`).then((r) => r.json()),
          fetch(`${base}/api/fee-preview?total=220`).then((r) => r.json()),
        ]);
        if (!cancelled) setDeployInfo({ build: root?.build, fee });
      } catch {
        /* ignore — login still works */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (challenge) {
        await completeOtpLogin(challenge, otpCode.trim());
      } else {
        const data = await login(email.trim().toLowerCase(), password);
        if (data?.otp_required) {
          setChallenge(data.challenge || "");
          return;
        }
      }
      toast.success("Byenveni!");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erè koneksyon");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-brand-blue/20 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-brand-gold/10 blur-3xl" />
      </div>

      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        <LanguageSwitcher />
        <ThemeToggle />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="flex justify-center mb-8">
          <Logo size={56} />
        </div>
        <div className="rounded-2xl border border-border bg-card p-8 shadow-2xl">
          <h1 className="font-heading text-2xl font-bold text-foreground">{t("admin_login_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">Aksè administratè sèlman</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">{t("email")}</Label>
              <div className="mt-1.5 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@tgs.com"
                  data-testid="admin-email-input"
                  className="pl-9 h-11"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password">{t("password")}</Label>
              <div className="mt-1.5 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="admin-password-input"
                  className="pl-9 pr-11 h-11"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword((open) => !open)}
                  aria-label={showPassword ? "Kache modpas la" : "Montre modpas la"}
                  data-testid="admin-password-toggle"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {challenge && (
              <div>
                <Label htmlFor="otp">Kòd otantifikatè</Label>
                <div className="mt-1.5 relative">
                  <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="otp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    placeholder="6 chif"
                    data-testid="admin-otp-input"
                    className="pl-9 h-11 tracking-widest"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Ouvri aplikasyon otantifikatè a epi antre kòd 6 chif la.
                </p>
              </div>
            )}
            <Button
              type="submit"
              disabled={loading || (challenge && otpCode.trim().length < 6)}
              data-testid="admin-login-btn"
              className="w-full h-11 bg-brand-blue hover:bg-brand-blueHover text-white font-semibold"
            >
              {loading ? t("loading") : challenge ? "Verifye kòd la" : t("login")}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link
              to="/admin/forgot-password"
              className="text-sm text-brand-blue hover:underline"
              data-testid="admin-forgot-password-link"
            >
              {t("forgot_password")}
            </Link>
          </div>

          <div className="mt-6 text-center text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">← Retounen nan swiv transfè</Link>
          </div>
          {deployInfo?.build && (
            <div className="mt-4 rounded-lg border border-border bg-secondary/30 px-3 py-2 text-[11px] text-muted-foreground" data-testid="deploy-info">
              <div>Build: <span className="font-mono text-foreground">{deployInfo.build}</span></div>
              {deployInfo.fee?.service_fee != null && (
                <div className="mt-1">
                  Tès $220: frè ${Number(deployInfo.fee.service_fee).toFixed(2)} → pou bay ${Number(deployInfo.fee.net_amount).toFixed(2)}
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
